import { useState, useEffect } from 'react';
import {
  Grid3X3, Loader2, AlertTriangle, TrendingUp, TrendingDown, Activity, Info,
  Sparkles, RefreshCw, Calendar,
} from 'lucide-react';
import { getStrategyHeatmap } from '../services/api';
import { useCurrency } from '../hooks/useCurrency';
import { InfoTip } from '../components/ui';

const SOURCES = [
  { id: 'all',          label: 'All sources' },
  { id: 'smart_trade',  label: 'Smart Trade' },
  { id: 'grid_bot',     label: 'Grid Bot' },
  { id: 'dca_safety',   label: 'DCA Safety' },
  { id: 'cex_trading',  label: 'CEX Trading' },
];

const SOURCE_LABEL = {
  smart_trade: 'Smart Trade',
  grid_bot:    'Grid Bot',
  dca_safety:  'DCA Safety',
  cex_trading: 'CEX Trading (manual)',
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function StrategyHeatmapPage() {
  const { format } = useCurrency();
  const [source, setSource] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await getStrategyHeatmap(source);
      setData(res?.data || res);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [source]);

  return (
    <div className="space-y-6 animate-fade-in max-w-6xl">
      {/* Premium hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-violet-500/10 via-dark-800/80 to-dark-900 rounded-2xl border border-violet-500/20 p-6">
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-violet-400/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-emerald-400/5 blur-3xl pointer-events-none" />
        <div className="relative flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-violet-500/20 border border-violet-400/30 flex items-center justify-center flex-shrink-0">
              <Grid3X3 className="w-6 h-6 text-violet-400" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-bold text-white">Strategy Performance Heatmap</h1>
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 font-mono flex items-center gap-1">
                  Real fills
                  <InfoTip side="bottom">Only CLOSED round-trips count (paired BUY → SELL or Smart Trade exits). Open positions, pure buys, and unsold accumulation do not appear here.</InfoTip>
                </span>
              </div>
              <p className="text-xs text-violet-200/70 mt-1 flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" /> P&L by hour-of-day × day-of-week · Reveals when your strategy is most profitable
              </p>
            </div>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-violet-400/30 bg-violet-500/10 text-violet-300 hover:bg-violet-500/20 disabled:opacity-50 text-xs font-medium"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Source pills — premium look */}
      <div className="bg-dark-800/40 rounded-xl border border-dark-700/40 p-1.5 flex gap-1 flex-wrap">
        {SOURCES.map(s => (
          <button
            key={s.id}
            onClick={() => setSource(s.id)}
            className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-all ${
              source === s.id
                ? 'bg-violet-500/20 text-violet-300 border border-violet-400/40'
                : 'text-gray-400 hover:text-white hover:bg-dark-700/40 border border-transparent'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-4 text-red-400 text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-violet-400" /></div>
      ) : data?.insufficient ? (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-6">
          <div className="text-center mb-4">
            <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
            <p className="text-amber-400 font-bold mb-1">Insufficient closed round-trips</p>
            <p className="text-sm text-gray-400">{data.message}</p>
          </div>

          {data.breakdown && data.breakdown.length > 0 && (
            <div className="bg-dark-800/50 rounded-xl p-4 max-w-md mx-auto">
              <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 font-bold">Breakdown by source</p>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-gray-500">
                    <th className="text-left font-normal pb-1">Source</th>
                    <th className="text-right font-normal pb-1">Entries</th>
                    <th className="text-right font-normal pb-1">Closed round-trips</th>
                  </tr>
                </thead>
                <tbody>
                  {data.breakdown.map(b => (
                    <tr key={b.source} className="border-t border-dark-700/50">
                      <td className="py-1.5 text-gray-300">{SOURCE_LABEL[b.source] || b.source}</td>
                      <td className="py-1.5 text-right text-gray-400 font-mono">{b.entriesFound}</td>
                      <td className={`py-1.5 text-right font-mono ${b.closedRoundTrips > 0 ? 'text-emerald-400' : 'text-gray-500'}`}>{b.closedRoundTrips}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[10px] text-gray-500 mt-3 leading-relaxed">
                A "closed round-trip" means a BUY that was paired with a later SELL (or a Smart Trade that closed via TP/SL/manual). Open positions don't count until they close. Pure buys (e.g. accumulating BTC long-term) won't show up either.
              </p>
            </div>
          )}

          <p className="text-xs text-gray-500 mt-3 text-center">As your bots fire round-trips, this heatmap fills in. Come back after you have more closed trades.</p>
        </div>
      ) : data ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Stat
              label="Total realized P&L"
              value={format(data.totalPnl)}
              accent={data.totalPnl > 0 ? 'emerald' : data.totalPnl < 0 ? 'red' : undefined}
              subtext={source === 'all' ? 'across all strategies' : SOURCE_LABEL[source] || source}
              tooltip="Sum of profit/loss from every closed round-trip in this source filter. Excludes open positions and unrealized P&L."
            />
            <Stat
              label="Closed round-trips"
              value={data.totalTrades}
              subtext={`from real fills`}
              tooltip="Count of paired BUY→SELL or Smart Trade exits. Need ≥ 50 across ≥ 2 weeks for patterns to be statistically meaningful."
            />
            <Stat
              label="Win rate"
              value={`${data.overallWinRate.toFixed(1)}%`}
              accent={data.overallWinRate >= 60 ? 'emerald' : data.overallWinRate < 40 ? 'red' : undefined}
              subtext={`${Math.round((data.overallWinRate / 100) * data.totalTrades)}W / ${data.totalTrades - Math.round((data.overallWinRate / 100) * data.totalTrades)}L`}
              tooltip="% of trades with positive P&L. Win rate ≥ 60% is strong, < 40% means average win must significantly exceed average loss."
            />
          </div>

          {/* Per-source breakdown — only shown when viewing "All sources" so the
              user can see how the aggregate P&L decomposes (and reconcile
              against each bot's own dashboard). When filtered to a single
              source the aggregate IS the breakdown, so we hide it. */}
          {source === 'all' && Array.isArray(data.sourceBreakdown) && data.sourceBreakdown.length > 1 && (
            <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Activity className="w-4 h-4 text-violet-400" />
                <p className="text-xs uppercase tracking-wider text-gray-400">P&L decomposition · by source</p>
              </div>
              <p className="text-[11px] text-gray-500 mb-3">
                Aggregate above includes every closed round-trip across all your strategies. If your Grid Bot dashboard shows a different number, that's because it only sees its own pairs — this view sums them all.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {data.sourceBreakdown.map(s => (
                  <button
                    key={s.source}
                    onClick={() => setSource(s.source)}
                    className="text-left flex items-center justify-between gap-3 bg-dark-900/60 rounded-lg px-3 py-2 border border-dark-700/40 hover:border-violet-500/40 transition"
                  >
                    <div>
                      <p className="text-xs font-semibold text-white">{SOURCE_LABEL[s.source] || s.source}</p>
                      <p className="text-[10px] text-gray-500 font-mono">{s.wins}W / {s.losses}L · {s.winRate}% win</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-mono font-bold ${s.totalPnl > 0 ? 'text-emerald-400' : s.totalPnl < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                        {s.totalPnl > 0 ? '+' : ''}{format(s.totalPnl)}
                      </p>
                      <p className="text-[10px] text-gray-500 font-mono">{s.tradeCount} trades</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {data.bestCell && data.bestCell.totalPnl > 0 && (
            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-2xl p-4 flex items-start gap-3">
              <TrendingUp className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="text-emerald-400 font-bold">Best window</p>
                <p className="text-gray-300">
                  {DAYS[data.bestCell.dayOfWeek]} at {String(data.bestCell.hour).padStart(2, '0')}:00 UTC — {format(data.bestCell.totalPnl)} across {data.bestCell.tradeCount} trades ({data.bestCell.winRate?.toFixed(0)}% win rate)
                </p>
              </div>
            </div>
          )}

          {data.worstCell && data.worstCell.totalPnl < 0 && (
            <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-4 flex items-start gap-3">
              <TrendingDown className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="text-red-400 font-bold">Worst window</p>
                <p className="text-gray-300">
                  {DAYS[data.worstCell.dayOfWeek]} at {String(data.worstCell.hour).padStart(2, '0')}:00 UTC — {format(data.worstCell.totalPnl)} across {data.worstCell.tradeCount} trades. Consider pausing bots in this window.
                </p>
              </div>
            </div>
          )}

          <Heatmap cells={data.cells} format={format} />

          <p className="text-[10px] text-gray-500 flex items-start gap-1.5">
            <Info className="w-3 h-3 flex-shrink-0 mt-0.5" />
            All times UTC. Source: real fill records from your selected systems. {data.totalTrades} trades aggregated. Last computed {new Date(data.computedAt).toLocaleString()}.
          </p>
        </>
      ) : null}
    </div>
  );
}

function Heatmap({ cells, format }) {
  if (!cells) return null;

  const maxAbs = Math.max(1, ...cells.map(c => Math.abs(c.totalPnl)));

  function cellColor(pnl, tradeCount) {
    if (tradeCount === 0) return 'bg-dark-900/40';
    const intensity = Math.min(1, Math.abs(pnl) / maxAbs);
    if (pnl > 0) {
      if (intensity < 0.25) return 'bg-emerald-500/25';
      if (intensity < 0.5)  return 'bg-emerald-500/45';
      if (intensity < 0.75) return 'bg-emerald-500/65';
      return 'bg-emerald-500/85';
    }
    if (pnl < 0) {
      if (intensity < 0.25) return 'bg-red-500/25';
      if (intensity < 0.5)  return 'bg-red-500/45';
      if (intensity < 0.75) return 'bg-red-500/65';
      return 'bg-red-500/85';
    }
    return 'bg-dark-900/40';
  }

  // Group cells by [dayOfWeek][hour] for grid render + compute row/column totals
  const grid = Array.from({ length: 7 }, (_, d) =>
    Array.from({ length: 24 }, (_, h) => cells.find(c => c.dayOfWeek === d && c.hour === h))
  );
  const rowTotals = grid.map(row => row.reduce((s, c) => s + (c?.totalPnl || 0), 0));
  const colTotals = Array.from({ length: 24 }, (_, h) =>
    grid.reduce((s, row) => s + (row[h]?.totalPnl || 0), 0)
  );
  const rowTrades = grid.map(row => row.reduce((s, c) => s + (c?.tradeCount || 0), 0));

  return (
    <div className="bg-gradient-to-br from-dark-800/80 to-dark-900/40 rounded-2xl border border-dark-700/50 p-5 overflow-x-auto">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-violet-400" />
          <p className="text-xs uppercase tracking-wider text-gray-400 font-medium">Hour × day matrix · UTC</p>
        </div>
        <div className="flex items-center gap-2 text-[10px] text-gray-500">
          <span>Loss</span>
          <div className="flex gap-0.5">
            <div className="w-3 h-3 rounded bg-red-500/85"></div>
            <div className="w-3 h-3 rounded bg-red-500/45"></div>
            <div className="w-3 h-3 rounded bg-dark-900/40 border border-dark-700/40"></div>
            <div className="w-3 h-3 rounded bg-emerald-500/45"></div>
            <div className="w-3 h-3 rounded bg-emerald-500/85"></div>
          </div>
          <span>Profit</span>
        </div>
      </div>
      <table className="w-full text-[10px] border-separate border-spacing-1">
        <thead>
          <tr>
            <th className="p-1 w-10"></th>
            {Array.from({ length: 24 }, (_, h) => (
              <th key={h} className="p-1 text-gray-500 font-mono">{String(h).padStart(2, '0')}</th>
            ))}
            <th className="p-1 text-gray-400 font-mono text-[10px] pl-2">∑ day</th>
          </tr>
        </thead>
        <tbody>
          {DAYS.map((day, d) => (
            <tr key={day}>
              <td className="p-1 text-gray-300 font-bold text-right pr-2">{day}</td>
              {grid[d].map((c, h) => {
                const has = c && c.tradeCount > 0;
                return (
                  <td key={h} className="p-0">
                    <div
                      className={`aspect-square rounded ${cellColor(c?.totalPnl || 0, c?.tradeCount || 0)} flex items-center justify-center cursor-help text-[9px] font-mono ${has ? 'text-white font-semibold' : 'text-gray-700'}`}
                      title={has
                        ? `${day} ${String(h).padStart(2, '0')}:00 UTC\n${format(c.totalPnl)} · ${c.tradeCount} trade${c.tradeCount > 1 ? 's' : ''} · ${c.winRate?.toFixed(0)}% win`
                        : `${day} ${String(h).padStart(2, '0')}:00 UTC — no trades`}
                    >
                      {has ? c.tradeCount : ''}
                    </div>
                  </td>
                );
              })}
              <td className={`p-1 text-right font-mono text-[10px] pl-2 ${rowTotals[d] > 0 ? 'text-emerald-400' : rowTotals[d] < 0 ? 'text-red-400' : 'text-gray-600'}`}>
                {rowTrades[d] > 0 ? format(rowTotals[d]) : ''}
              </td>
            </tr>
          ))}
          {/* Column totals row */}
          <tr>
            <td className="p-1 text-right pr-2 text-gray-400 text-[10px] font-mono">∑ hr</td>
            {colTotals.map((v, h) => (
              <td key={h} className={`p-0.5 text-center font-mono text-[9px] ${v > 0 ? 'text-emerald-400/70' : v < 0 ? 'text-red-400/70' : 'text-gray-700'}`}>
                {Math.abs(v) >= 0.005 ? format(v).replace('$', '') : ''}
              </td>
            ))}
            <td></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function Stat({ label, value, accent, subtext, tooltip }) {
  const colorMap = { emerald: 'text-emerald-400', red: 'text-red-400' };
  return (
    <div className="bg-gradient-to-br from-dark-800/80 to-dark-900/40 rounded-xl border border-dark-700/50 p-4 hover:border-dark-600 transition">
      <p className="text-[10px] uppercase tracking-wider text-gray-500 font-medium flex items-center gap-1">
        {label}
        {tooltip && <InfoTip side="top">{tooltip}</InfoTip>}
      </p>
      <p className={`text-2xl font-bold font-mono mt-1 ${colorMap[accent] || 'text-white'}`}>{value}</p>
      {subtext && <p className="text-[10px] text-gray-500 mt-0.5">{subtext}</p>}
    </div>
  );
}
